import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import { useDebugValue, useEffect } from "react";
import isEqual from "lodash/isEqual";
import isFunction from "lodash/isFunction";
import useUpdate from "../useUpdate";
import usePersist from "../usePersist";
import useInterval from "../useInterval";
import useSafeState from "../useSafeState";
import useEventListener from "../useEventListener";
import useMemoizedValue from "../useMemoizedValue";
function createStorageHook(storage) {
  function useStorage(key, options) {
    var _ref = options || {},
      defaultValueOption = _ref.defaultValue,
      _ref$polling = _ref.polling,
      polling = _ref$polling === void 0 ? false : _ref$polling,
      _ref$pollingInterval = _ref.pollingInterval,
      pollingInterval = _ref$pollingInterval === void 0 ? 100 : _ref$pollingInterval,
      _ref$compare = _ref.compare,
      compare = _ref$compare === void 0 ? isEqual : _ref$compare,
      validate = _ref.validate,
      _ref$serialize = _ref.serialize,
      serialize = _ref$serialize === void 0 ? JSON.stringify : _ref$serialize,
      _ref$deserialize = _ref.deserialize,
      deserialize = _ref$deserialize === void 0 ? JSON.parse : _ref$deserialize;
    var defaultValue = useMemoizedValue(defaultValueOption, compare);
    function getStoredValue() {
      try {
        var _raw = storage === null || storage === void 0 ? void 0 : storage.getItem(key);
        if (_raw != null) {
          var _value = deserialize(_raw);
          if (validate) {
            return validate(_value) ? _value : defaultValue;
          } else {
            return _value;
          }
        }
      } catch (error) {
        console.error(error);
      }
      return defaultValue;
    }
    var _useSafeState = useSafeState(getStoredValue),
      _useSafeState2 = _slicedToArray(_useSafeState, 2),
      value = _useSafeState2[0],
      setValue = _useSafeState2[1];
    var setStorage = usePersist(function (newValue) {
      setValue(function (prevValue) {
        newValue = isFunction(newValue) ? newValue(prevValue) : newValue;
        if (newValue === undefined) {
          storage === null || storage === void 0 ? void 0 : storage.removeItem(key);
          return defaultValue;
        }
        try {
          storage === null || storage === void 0 ? void 0 : storage.setItem(key, serialize(newValue));
        } catch (error) {
          console.error(error);
        }
        return newValue;
      });
    });
    var handleChange = function handleChange() {
      var newValue = getStoredValue();
      if (!compare(newValue, value)) {
        setValue(newValue);
      }
    };
    var _useInterval = useInterval(handleChange, pollingInterval),
      _useInterval2 = _slicedToArray(_useInterval, 2),
      startPolling = _useInterval2[0],
      cancelPolling = _useInterval2[1];
    useEffect(function () {
      cancelPolling();
      if (polling) {
        startPolling();
      }
    }, [polling] // eslint-disable-line
    );

    useEventListener(function () {
      return window;
    }, "storage", handleChange);
    useUpdate(function () {
      cancelPolling();
      setValue(getStoredValue());
      if (polling) {
        startPolling();
      }
    }, [key]);
    useUpdate(function () {
      if ((storage === null || storage === void 0 ? void 0 : storage.getItem(key)) == null && !compare(defaultValue, value)) {
        setValue(defaultValue);
      }
    }, [defaultValue]);
    useDebugValue(value);
    useDebugValue(key, function (key) {
      return "(raw) " + (storage === null || storage === void 0 ? void 0 : storage.getItem(key));
    });
    return [value, setStorage];
  }
  return useStorage;
}
export default createStorageHook;